/**
 * Frontend entry point.
 *
 * Routing: History API, clean URLs — /login, /register, /forgot-password,
 * /reset-password?token=..., /verify-email?token=..., /play (authenticated).
 *
 * Tankodromo: /play/* is the Vue game app (engine/bootstrap.ts) and the
 * public pages (/, /news/:slug, /legal/:slug) are the landing site
 * (engine/landing). Both are separate lazy chunks, so the auth screens and
 * the landing's first paint never download the game. Once one is mounted,
 * its own vue-router owns navigation inside it, so this router does not
 * remount it on in-app link clicks or back/forward.
 *
 * /admin-panel/* is the admin panel (engine/admin), a third lazy chunk.
 */
import { bootstrapSession, logout } from './core/auth/auth-api';
import { renderLogin } from './core/auth/login-screen';
import { renderRegister } from './core/auth/register-screen';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { authStore } from './core/state/auth-store';
import { connectSocket, disconnectSocket } from './core/network/socket';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
import { isGameRoute, isLandingRoute } from './engine/game-route';
import { isAdminRoute } from './engine/admin/admin-route';
import { syncAuthHome } from './engine/auth-home';
import { enhanceRegister, recordTermsAcceptance } from './engine/auth-register';

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;
/** Which lazy app is mounted in #app, if any. */
let mounted: 'game' | 'landing' | 'admin' | null = null;
/** Bumped on every render so a chunk that loads late never mounts over a newer route. */
let renderSeq = 0;
let unmountGame: (() => void) | null = null;
/** Where to go after sign-in when a game link was opened signed out. */
const RETURN_KEY = 'tkd.returnTo';

export function navigate(to: string) {
  if (location.pathname !== to) history.pushState(null, '', to);
  render();
}

function render() {
  const path = location.pathname;
  syncAuthHome(path, navigate);
  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  // In-app navigation (game scenes and modals, landing pages) belongs to vue-router.
  if (mounted === 'game' && isGameRoute(path) && user?.emailVerified) return;
  if (mounted === 'landing' && isLandingRoute(path)) return;
  if (mounted === 'admin' && isAdminRoute(path) && user) return;

  teardown?.();
  teardown = null;
  mounted = null;
  const seq = ++renderSeq;

  const afterAuth = () => {
    const u = authStore.get().user;
    if (u && !u.emailVerified) { navigate('/check-email'); return; }
    connectSocket();
    // A confirmation link opened while signed out comes back here after sign-in.
    let back: string | null = null;
    try {
      back = sessionStorage.getItem(RETURN_KEY);
      sessionStorage.removeItem(RETURN_KEY);
    } catch {
      /* storage blocked: start at the base */
    }
    if (back?.startsWith('/play/')) {
      history.replaceState(null, '', back);
      render();
      return;
    }
    navigate('/play');
  };

  if (isGameRoute(path)) {
    if (!user) { navigate('/login'); return; }
    if (!user.emailVerified) { navigate('/check-email'); return; }
    connectSocket();
    mounted = 'game';
    void import('./engine/bootstrap').then((m) => {
      if (seq !== renderSeq) return;
      unmountGame = m.unmountGame;
      teardown = m.mountGame(app, user, () => { logout(); disconnectSocket(); navigate('/login'); });
    });
    return;
  }

  // Admin panel: its own lazy chunk; the panel itself refuses non-ADMIN roles.
  if (isAdminRoute(path)) {
    if (!user) { navigate('/login'); return; }
    mounted = 'admin';
    void import('./engine/admin/mount').then((m) => {
      if (seq !== renderSeq) return;
      teardown = m.mountAdmin(app, user);
    });
    return;
  }

  if (isLandingRoute(path)) {
    mounted = 'landing';
    void import('./engine/landing/mount').then((m) => {
      if (seq !== renderSeq) return;
      teardown = m.mountLanding(app, { signedIn: () => !!authStore.get().user });
    });
    return;
  }

  // Core payments return here (core's default returnPath); the game shows it.
  if (path === '/payment-result') {
    history.replaceState(null, '', `/play/payment-result${location.search}`);
    render();
    return;
  }

  // Account restart/delete confirmation e-mails link here (game-api 21); the game confirms it.
  if (path === '/account/confirm') {
    const to = `/play/account-confirm${location.search}`;
    if (!user) {
      try {
        sessionStorage.setItem(RETURN_KEY, to);
      } catch {
        /* storage blocked: the player opens the link again after signing in */
      }
      navigate('/login');
      return;
    }
    history.replaceState(null, '', to);
    render();
    return;
  }

  switch (path) {
    case '/login':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      teardown = renderLogin(app, afterAuth);
      break;
    case '/register':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return; }
      {
        // Game additions to core's form: confirm password + the terms checkbox (engine/auth-register.ts).
        const stopCore = renderRegister(app, () => {
          void recordTermsAcceptance();
          afterAuth();
        });
        const stopGame = enhanceRegister(app);
        teardown = () => {
          stopGame();
          stopCore();
        };
      }
      break;
    case '/forgot-password':
      if (user) { navigate('/play'); return; }
      teardown = renderForgotPassword(app);
      break;
    case '/reset-password':
      teardown = renderResetPassword(app, params.get('token') ?? '', () => navigate('/login'));
      break;
    case '/verify-email':
      teardown = renderVerifyEmail(app, params.get('token') ?? '');
      break;
    case '/check-email':
      if (!user) { navigate('/login'); return; }
      teardown = renderCheckEmail(app, user.email, () => { disconnectSocket(); navigate('/login'); });
      break;
    default:
      navigate('/login');
  }
}

window.addEventListener('popstate', render);

document.addEventListener('click', (e) => {
  // A handler that already took the click (vue-router links) wins.
  if (e.defaultPrevented) return;
  const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target || a.origin !== location.origin) return;
  e.preventDefault();
  navigate(a.pathname + a.search);
});

(async () => {
  // Fetch site-info first — drives preview-mode gating throughout the app
  await fetchSiteInfo();
  if (isPreviewBuild()) stripBrandMeta();

  await bootstrapSession();
  render();

  authStore.subscribe((s) => {
    if (!s.user && (isGameRoute(location.pathname) || isAdminRoute(location.pathname))) navigate('/login');
  });
})();

window.addEventListener('beforeunload', () => {
  teardown?.();
  unmountGame?.();
});
